import { learningPathLessons } from './data';
import type { Confidence, MistakeItem, ProgressBackup, ProgressState } from './types';

export const PROGRESS_KEY = 'english-coach-beginner-c1-progress-v2';

const today = () => new Date().toISOString().slice(0, 10);

export function createDefaultProgress(): ProgressState {
  const firstLesson = learningPathLessons[0]?.id ?? 'beginner-01';
  return {
    currentLevel: 'Beginner',
    currentLessonId: firstLesson,
    unlockedLessonIds: [firstLesson],
    completedLessonIds: [],
    completedStepsByLesson: {},
    examScores: {},
    mistakes: [],
    vocabularyConfidence: {},
    phraseConfidence: {},
    grammarProgress: {},
    writingDrafts: {},
    speakingRatings: {},
    streak: 0,
    lastStudyDate: null,
  };
}

export function loadProgress(): ProgressState {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return createDefaultProgress();
    return normalizeProgress(JSON.parse(raw) as Partial<ProgressState>);
  } catch {
    return createDefaultProgress();
  }
}

export function saveProgress(progress: ProgressState) {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
}

export function normalizeProgress(progress: Partial<ProgressState>): ProgressState {
  const base = createDefaultProgress();
  return {
    ...base,
    ...progress,
    unlockedLessonIds: progress.unlockedLessonIds?.length ? progress.unlockedLessonIds : base.unlockedLessonIds,
    completedLessonIds: progress.completedLessonIds ?? [],
    completedStepsByLesson: progress.completedStepsByLesson ?? {},
    examScores: progress.examScores ?? {},
    mistakes: progress.mistakes ?? [],
    vocabularyConfidence: progress.vocabularyConfidence ?? {},
    phraseConfidence: progress.phraseConfidence ?? {},
    grammarProgress: progress.grammarProgress ?? {},
    writingDrafts: progress.writingDrafts ?? {},
    speakingRatings: progress.speakingRatings ?? {},
  };
}

export function touchStudyDay(progress: ProgressState): ProgressState {
  const date = today();
  if (progress.lastStudyDate === date) return progress;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const streak = progress.lastStudyDate === yesterday.toISOString().slice(0, 10) ? progress.streak + 1 : 1;
  return { ...progress, lastStudyDate: date, streak };
}

export function markStep(progress: ProgressState, lessonId: string, step: string): ProgressState {
  const touched = touchStudyDay(progress);
  const steps = new Set(touched.completedStepsByLesson[lessonId] ?? []);
  steps.add(step);
  return {
    ...touched,
    currentLessonId: lessonId,
    completedStepsByLesson: { ...touched.completedStepsByLesson, [lessonId]: [...steps] },
  };
}

export function addMistake(progress: ProgressState, mistake: Omit<MistakeItem, 'id' | 'dateAdded' | 'reviewStatus'>): ProgressState {
  const duplicate = progress.mistakes.some((item) => item.source === mistake.source && item.item === mistake.item && item.correctAnswer === mistake.correctAnswer);
  if (duplicate) return touchStudyDay(progress);
  return {
    ...touchStudyDay(progress),
    mistakes: [{ ...mistake, id: crypto.randomUUID(), dateAdded: new Date().toISOString(), reviewStatus: 'Hard' as const }, ...progress.mistakes].slice(0, 300),
  };
}

export function rateMistake(progress: ProgressState, id: string, rating: Confidence): ProgressState {
  return {
    ...touchStudyDay(progress),
    mistakes: rating === 'Easy'
      ? progress.mistakes.filter((item) => item.id !== id)
      : progress.mistakes.map((item) => item.id === id ? { ...item, reviewStatus: rating } : item),
  };
}

export function completeLesson(progress: ProgressState, lessonId: string, score: number): ProgressState {
  const index = learningPathLessons.findIndex((lesson) => lesson.id === lessonId);
  const next = learningPathLessons[index + 1]?.id;
  const unlocked = new Set(progress.unlockedLessonIds);
  if (next) unlocked.add(next);
  const completed = new Set(progress.completedLessonIds);
  completed.add(lessonId);
  const nextLesson = next ?? lessonId;
  const currentLevel = learningPathLessons.find((lesson) => lesson.id === nextLesson)?.level ?? progress.currentLevel;
  return {
    ...touchStudyDay(progress),
    currentLevel,
    currentLessonId: nextLesson,
    unlockedLessonIds: [...unlocked],
    completedLessonIds: [...completed],
    examScores: { ...progress.examScores, [lessonId]: score },
  };
}

export function saveExamScore(progress: ProgressState, lessonId: string, score: number): ProgressState {
  return { ...touchStudyDay(progress), examScores: { ...progress.examScores, [lessonId]: score } };
}

export function exportBackup(progress: ProgressState): string {
  const backup: ProgressBackup = {
    app: 'english-coach-beginner-c1',
    version: 2,
    exportedAt: new Date().toISOString(),
    progress,
  };
  return btoa(unescape(encodeURIComponent(JSON.stringify(backup))));
}

export function importBackup(code: string): ProgressState {
  const parsed = JSON.parse(decodeURIComponent(escape(atob(code.trim())))) as ProgressBackup;
  if (parsed.app !== 'english-coach-beginner-c1' || !parsed.progress) throw new Error('Invalid backup code');
  return normalizeProgress(parsed.progress);
}

export function resetProgress() {
  localStorage.removeItem(PROGRESS_KEY);
}
